import { useEffect, useRef, useState, type ReactNode } from "react";

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  /** Delay in ms before the animation starts after the element enters viewport */
  delay?: number;
  /** How much of the element must be visible to trigger (0-1), default 0.08 */
  threshold?: number;
  /** Whether to only animate once (default true) */
  once?: boolean;
  /** HTML tag to render as wrapper, default 'div' */
  as?: keyof JSX.IntrinsicElements;
  /** Whether to add will-change for GPU acceleration (default true) */
  gpu?: boolean;
}

export default function ScrollReveal({
  children,
  className = "",
  delay = 0,
  threshold = 0.08,
  once = true,
  as: Tag = "div",
  gpu = true,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, once]);

  return (
    <Tag
      ref={ref as any}
      className={`scroll-reveal ${isVisible ? "scroll-reveal-visible" : "scroll-reveal-hidden"} ${gpu ? "will-change-transform" : ""} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}